import type { MutableRefObject, ReactNode } from "react";
import {
	createContext,
	useCallback,
	useContext,
	useMemo,
	useRef,
	useState,
} from "react";

import type {
	AddressBookContactDetail,
	AddressBookItemSummary,
} from "~/shared/schemas/address-book-schema.ts";

import { IDENTITY_ADDRESS_FIELDS } from "../utils/address-identity-fields.ts";

export type SelectedAddressBookItems = {
	pickup: AddressBookItemSummary | null;
	delivery: AddressBookItemSummary | null;
};

export type AddressChangeSource = "user" | "auto";

export type QuoteAddressContextType = {
	selectedAddressBookItems: SelectedAddressBookItems;
	setSelectedAddressBookItem: (
		addressType: keyof SelectedAddressBookItems,
		address: AddressBookItemSummary,
		source?: AddressChangeSource,
	) => void;
	updateSelectedAddressBookItem: (
		addressType: keyof SelectedAddressBookItems,
		field: string,
		value: string | undefined,
	) => void;
	resetSelectedAddressBookItem: (
		addressType: keyof SelectedAddressBookItems,
	) => void;
	resetSelectedAddressBookItems: () => void;
	addressChangeSourceRef: MutableRefObject<AddressChangeSource>;
};

type QuoteAddressProviderProps = {
	readonly children: ReactNode;
};

const getDefaultAddressBookItemDetail = (
	stopType: number,
): AddressBookContactDetail => {
	return {
		addressBookItemId: 0,
		rowVersion: null,
		contactDetailId: 0,
		contactName: "",
		contactPhone: "",
		contactExtension: "",
		hoursOpen: "",
		hoursClose: "",
		stopType: stopType,
		mode: 0,
		timeZone: "",
		specialInstructions: null,
		accessorials: null,
	};
};

export const defaultAddressBookItem: AddressBookItemSummary = {
	addressBookItemId: 0,
	companyName: null,
	address1: "",
	address2: "",
	postalCode: "",
	city: "",
	stateProvince: "",
	country: "",
	createdDate: "",
	createdByCustomerId: null,
	createdByInternalId: null,
	customerId: 0,
	isValidated: null,
	legacyAddressId: null,
	isLegacy: false,
	rowVersion: null,
	details: [
		getDefaultAddressBookItemDetail(1),
		getDefaultAddressBookItemDetail(2),
	],
};

const defaultAddressBookItems: SelectedAddressBookItems = {
	pickup: defaultAddressBookItem,
	delivery: defaultAddressBookItem,
};

export const QuoteAddressContext = createContext<
	QuoteAddressContextType | undefined
>(undefined);

export function QuoteAddressProvider({ children }: QuoteAddressProviderProps) {
	const [selectedAddressBookItems, setSelectedAddressBookItems] =
		useState<SelectedAddressBookItems>(defaultAddressBookItems);
	const addressChangeSourceRef = useRef<AddressChangeSource>("user");

	const setSelectedAddressBookItem = useCallback(
		(
			addressType: keyof SelectedAddressBookItems,
			address: AddressBookItemSummary,
			source: AddressChangeSource = "user",
		) => {
			addressChangeSourceRef.current = source;
			setSelectedAddressBookItems((prev) => ({
				...prev,
				[addressType]: address,
			}));
		},
		[],
	);

	const updateSelectedAddressBookItem = useCallback(
		(
			addressType: keyof SelectedAddressBookItems,
			field: string,
			value: string | undefined,
		) => {
			setSelectedAddressBookItems((prev) => {
				const currentItem = prev[addressType];
				if (!currentItem) return prev;

				// Handle fields that are on the details array (contact info)
				const detailFields = [
					"contactName",
					"contactPhone",
					"contactExtension",
					"hoursOpen",
					"hoursClose",
					"puNumber",
					"deliveryPO",
				];

				if (!currentItem.details.some((d) => d.stopType == 1)) {
					currentItem.details.push(getDefaultAddressBookItemDetail(1));
				}
				if (!currentItem.details.some((d) => d.stopType == 2)) {
					currentItem.details.push(getDefaultAddressBookItemDetail(2));
				}

				if (detailFields.includes(field)) {
					const targetStopType = addressType === "pickup" ? 1 : 2;
					const updatedDetails = currentItem.details.map((detail) => {
						if (detail.stopType === targetStopType) {
							return { ...detail, [field]: value };
						}
						return detail;
					});
					return {
						...prev,
						[addressType]: { ...currentItem, details: updatedDetails },
					};
				}

				// Handle top-level fields (companyName, address1, address2, etc.).
				// Editing an identity field detaches the stop from its saved address
				// (the data no longer equals the saved entry), so clear the
				// addressBookItemId to keep the invariant that a non-zero id means
				// "pristine saved address". Contact/hours edits (handled above) keep it.
				return {
					...prev,
					[addressType]: {
						...currentItem,
						...(IDENTITY_ADDRESS_FIELDS.has(field)
							? { addressBookItemId: 0 }
							: {}),
						[field]: value,
					},
				};
			});
		},
		[],
	);

	const resetSelectedAddressBookItem = useCallback(
		(addressType: keyof SelectedAddressBookItems) => {
			setSelectedAddressBookItems((prev) => ({
				...prev,
				[addressType]: null,
			}));
		},
		[],
	);

	const resetSelectedAddressBookItems = useCallback(() => {
		setSelectedAddressBookItems(defaultAddressBookItems);
	}, []);

	const value = useMemo(
		() => ({
			selectedAddressBookItems,
			setSelectedAddressBookItem,
			updateSelectedAddressBookItem,
			resetSelectedAddressBookItem,
			resetSelectedAddressBookItems,
			addressChangeSourceRef,
		}),
		[
			selectedAddressBookItems,
			setSelectedAddressBookItem,
			updateSelectedAddressBookItem,
			resetSelectedAddressBookItem,
			resetSelectedAddressBookItems,
		],
	);

	return (
		<QuoteAddressContext.Provider value={value}>
			{children}
		</QuoteAddressContext.Provider>
	);
}

export function useQuoteAddressContext(): QuoteAddressContextType {
	const context = useContext(QuoteAddressContext);
	if (!context) {
		throw new Error(
			"useQuoteAddressContext must be used within a QuoteAddressProvider",
		);
	}

	return context;
}
